/**
 * Addressing a stretch of the manuscript.
 *
 * The authoring assistant works on whatever the author pointed at — a paragraph, a span of
 * paragraphs, a subheading and the prose under it, a `^` section, a whole file. Those are all
 * one thing: a contiguous run of blocks, named in different ways.
 *
 * Deliberately not `AnnotationAnchor`. That type persists across edits and carries snapshots
 * so a moved annotation can be re-matched later; a ContentRange is resolved once, immediately,
 * against the revision the author is looking at, and then discarded. Same shape, different
 * lifetime — sharing one type would drag the repair machinery somewhere it isn't wanted.
 */

import type { Block, ContentAST, HeadingBlock } from './astTypes'
import { analyzeWritingSyntax } from './writingSyntax'

export type ContentRange =
  /** One block, or a run of them. `endBlockId` defaults to `startBlockId`. */
  | { type: 'blocks'; startBlockId: string; endBlockId?: string }
  /** A heading and everything under it, to the next heading of the same or higher level. */
  | { type: 'subtree'; headingBlockId: string }
  /** The whole source file. */
  | { type: 'file' }

/** Half-open block indices: `[startIdx, endIdx)`. */
export interface ResolvedRange {
  startIdx: number
  endIdx: number
}

/**
 * A heading and the blocks that belong to it, ending at the next heading of the same or
 * higher level. Unlike the `^` variant in `extractChapterSection`, this accepts any heading —
 * a plain subheading is a working unit even though it is not a ChapterSection.
 */
export function locateHeadingSubtreeRange(ast: ContentAST, headingBlockId: string): ResolvedRange | null {
  const section = analyzeWritingSyntax(ast.blocks).sections.find((s) => s.heading.id === headingBlockId)
  if (section) {
    return { startIdx: section.start, endIdx: section.end }
  }
  const blocks = ast.blocks
  const startIdx = blocks.findIndex((b) => b.id === headingBlockId && b.type === 'heading')
  if (startIdx === -1) {
    return null
  }

  const level = (blocks[startIdx] as HeadingBlock).level
  for (let i = startIdx + 1; i < blocks.length; i++) {
    const block = blocks[i]
    if (block.type === 'heading' && (block as HeadingBlock).level <= level) {
      return { startIdx, endIdx: i }
    }
  }
  return { startIdx, endIdx: blocks.length }
}

/** Resolve a range to block indices, or null when it names nothing in this AST. */
export function resolveContentRange(ast: ContentAST, range: ContentRange): ResolvedRange | null {
  if (range.type === 'file') {
    return { startIdx: 0, endIdx: ast.blocks.length }
  }

  if (range.type === 'subtree') {
    return locateHeadingSubtreeRange(ast, range.headingBlockId)
  }

  const startIdx = ast.blocks.findIndex((b) => b.id === range.startBlockId)
  if (startIdx === -1) {
    return null
  }
  if (!range.endBlockId || range.endBlockId === range.startBlockId) {
    return { startIdx, endIdx: startIdx + 1 }
  }

  const endIdx = ast.blocks.findIndex((b) => b.id === range.endBlockId)
  if (endIdx === -1 || endIdx < startIdx) {
    return null
  }
  return { startIdx, endIdx: endIdx + 1 }
}

/** The blocks a range covers. Empty when it resolves to nothing. */
export function blocksInRange(ast: ContentAST, range: ContentRange): Block[] {
  const resolved = resolveContentRange(ast, range)
  return resolved ? ast.blocks.slice(resolved.startIdx, resolved.endIdx) : []
}

/** Replace a range's blocks, returning a new AST. Null when the range does not resolve. */
export function replaceRange(ast: ContentAST, range: ContentRange, blocks: Block[]): ContentAST | null {
  const resolved = resolveContentRange(ast, range)
  if (!resolved) {
    return null
  }
  return {
    ...ast,
    meta: { ...ast.meta },
    blocks: [...ast.blocks.slice(0, resolved.startIdx), ...blocks, ...ast.blocks.slice(resolved.endIdx)],
  }
}
